import React from 'react'
import { DownArrowNoBG, UpArrowWhiteBG } from 'assests/GmsIcons';
import { CATEGORY, CATEGORY_TYPE, CO_SOLD_CATEGORY, CO_SOLD_CATEGORY_TYPE, CURATED, INVOICE_COUNT, LOADING, NO_CATEGORIES_FOUND, NO_CO_SOLD_CATEGORIES_FOUND } from '../constants/CategoryRelevancyConstants';
import TableSkeletonElement from 'Components/GMS/Products/components/TableSkeletonElement';

type CategoryRelevancyTableProps = {
    categories: Category[] | undefined;
    expandedCategories: Set<string>;
    coSoldCategories: CoSoldCategoryParent[];
    onCategoryExpand: (categoryId: string) => void;
    onToggleCuration: (coSoldCategoryId: string, parentCategoryId: string, isCurated: boolean) => void;
    isCuratedTab: boolean;
    rowDensity: RowDensity;
    loadingCategories: boolean;
    loadingCoSoldCategories: (categoryId: string) => boolean;
    handleSort: (column: SortOption) => void
    sortBy: SortOption
    sortOrder: SortOrder
}

const CategoryRelevancyTable = ({ categories, expandedCategories, coSoldCategories, onCategoryExpand, onToggleCuration, isCuratedTab, rowDensity, loadingCategories, loadingCoSoldCategories, sortBy, sortOrder, handleSort }: CategoryRelevancyTableProps) => {
    const getRowPadding = () => {
        switch (rowDensity) {
            case 'compact': return 'tw-py-1';
            case 'comfortable': return 'tw-py-3';
            default: return 'tw-py-2';
        }
    };

    const rowPadding = getRowPadding();

    const headerClasses = "tw-cursor-pointer tw-py-3 tw-text-left tw-text-sm tw-font-semibold tw-text-[#6B778C] tw-tracking-wider tw-whitespace-nowrap";
    const truncatedCell = "tw-truncate tw-max-w-0 tw-overflow-hidden tw-whitespace-nowrap";

    const getColumnWidth = (column: SortOption): string => {
        switch (column) {
            case 'invoice_count':
                return 'tw-w-[140px]';
            case 'is_curated':
                return 'tw-w-[100px]';
            default:
                {
                    const totalColumns = isCuratedTab ? 5 : 6;
                    const fixedColumns = isCuratedTab ? 1 : 2;
                    const flexColumns = totalColumns - fixedColumns;
                    const flexPercentage = Math.floor((100 - (80 + 100)) / flexColumns);
                    return `tw-w-[${flexPercentage}%]`;
                }
        }
    };

    const renderHeaderCell = (column: SortOption, label: string, indent: boolean = false) => (
        <th key={column} className={`${headerClasses} ${getColumnWidth(column)}`} onClick={() => handleSort(column)}>
            <div className={`tw-flex tw-items-center ${indent ? 'tw-ml-10' : ''}`}>
                <span>{label}</span>
                <span className="tw-ml-1">{sortElement(column)}</span>
            </div>
        </th>
    );

    const sortElement = (column: string) => {
        return sortBy === column ? (
            <span className="tw-ml-1">
                {sortOrder === 1 ? (
                    <i className="fas fa-sort-up" />
                ) : (
                    <i className="fas fa-sort-down" />
                )}
            </span>
        ) : (
            <i className="fas fa-sort" />
        );
    };

    const findCoSoldCategoriesForCategory = (categoryId: string): CoSoldCategory[] => {
        const parentCategory = coSoldCategories.find(cat => cat.category_id === categoryId);
        return parentCategory?.co_sold_categories || [];
    };

    if (loadingCategories || !categories) { return <div className=""><TableSkeletonElement /></div> }

    return (
        <div className="tw-border tw-border-solid tw-rounded-md tw-mt-3 tw-border-[#DFE0E6] tw-overflow-hidden tw-mb-4">
            <div className="table-container" style={{ width: '100%', height: 'auto', overflowX: 'auto' }}>
                <table className="tw-bg-zinc-200 tw-w-full tw-table-fixed">
                    <thead className="tw-sticky tw-top-0 tw-bg-zinc-200">
                        <tr>
                            {renderHeaderCell("category_name", CATEGORY, true)}
                            {renderHeaderCell("category_type_name", CATEGORY_TYPE)}
                            {renderHeaderCell("co_sold_category", CO_SOLD_CATEGORY)}
                            {renderHeaderCell("co_sold_category_type", CO_SOLD_CATEGORY_TYPE)}
                            {renderHeaderCell("invoice_count", INVOICE_COUNT)}
                            {!isCuratedTab && renderHeaderCell("is_curated", CURATED)}
                        </tr>
                    </thead>
                    <tbody>
                        {categories?.map(category => {
                            const isExpanded = expandedCategories?.has(category.category_id);
                            const coSoldItems: CoSoldCategory[] = findCoSoldCategoriesForCategory(category.category_id);

                            const parentCategory = coSoldCategories.find(cat => cat.category_id === category.category_id);

                            return (
                                <React.Fragment key={category.category_id}>
                                    {/* Category header row */}
                                    <tr
                                        className="tw-border-b-[0.5px] tw-border-zinc-400 tw-cursor-pointer tw-bg-zinc-300"
                                        onClick={() => onCategoryExpand(category.category_id)}
                                    >
                                        <td colSpan={isCuratedTab ? 5 : 6} className="tw-p-0">
                                            <div className="tw-flex tw-items-center tw-gap-2 tw-py-1 tw-pl-2">
                                                {!isExpanded ? <div className='tw-px-1.5'><DownArrowNoBG /></div> : <UpArrowWhiteBG />}
                                                <span className="tw-justify-start tw-text-slate-500 tw-text-sm tw-font-semibold">{category.category_name}</span>
                                            </div>
                                        </td>
                                    </tr>

                                    {/* Co-sold categories */}
                                    {isExpanded && (
                                        <>
                                            {loadingCoSoldCategories(category.category_id) ? (
                                                <tr>
                                                    <td colSpan={isCuratedTab ? 5 : 6} className="tw-p-4 tw-text-center tw-text-gray-500">{LOADING}</td>
                                                </tr>
                                            ) : coSoldItems.length === 0 ? (
                                                <tr>
                                                    <td colSpan={isCuratedTab ? 5 : 6} className="tw-p-4 tw-text-center tw-text-gray-500">{NO_CO_SOLD_CATEGORIES_FOUND}</td>
                                                </tr>
                                            ) : (
                                                coSoldItems.map((coSold: CoSoldCategory, index) => (
                                                    <tr
                                                        key={coSold.category_id}
                                                        className="hover:tw-bg-gray-50"
                                                    >
                                                        <td className={`${rowPadding} ${truncatedCell} ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"} tw-pl-10`} title={parentCategory?.category_name}>{parentCategory?.category_name}</td>
                                                        <td className={`${rowPadding} ${truncatedCell} ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"}`} title={parentCategory?.category_type_name}>{parentCategory?.category_type_name || ''}</td>
                                                        <td className={`${rowPadding} ${truncatedCell} ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"}`} title={coSold.category_name}>{coSold.category_name}</td>
                                                        <td className={`${rowPadding} ${truncatedCell} ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"}`} title={coSold.category_type_name}>{coSold.category_type_name}</td>
                                                        <td className={`${rowPadding} ${truncatedCell} ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"}`}>{coSold.invoice_count}</td>
                                                        {!isCuratedTab && (
                                                            <td className={`${rowPadding} ${truncatedCell} ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"} tw-items-center`}>
                                                                <button
                                                                    className={`tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-items-center tw-rounded-full ${coSold.is_curated ? 'tw-bg-green-600' : 'tw-bg-gray-300'}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onToggleCuration(coSold.category_id, category.category_id, !coSold.is_curated);
                                                                    }}
                                                                >
                                                                    <span
                                                                        className={`tw-inline-block tw-h-4 tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-transition ${coSold.is_curated ? 'tw-translate-x-6' : 'tw-translate-x-1'}`}
                                                                    />
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))
                                            )}
                                        </>
                                    )}
                                </React.Fragment>
                            );
                        })}

                        {!categories?.length && !loadingCategories && (
                            <tr>
                                <td colSpan={isCuratedTab ? 5 : 6} className="tw-p-4 tw-text-center tw-text-gray-500">{NO_CATEGORIES_FOUND}</td>
                            </tr>
                        )}

                        {loadingCategories && (
                            <tr>
                                <td colSpan={isCuratedTab ? 5 : 6} className="tw-p-4 tw-text-center tw-text-gray-500">{LOADING}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CategoryRelevancyTable